import isEmpty from "../../common/utils/isEmpty";
import { BriefcaseBusiness, MessageCircle, Phone, Star } from "lucide-react";
import { useMemo, useState } from "react";
import { FaFacebook, FaLinkedin } from "react-icons/fa";

const XIcon = ({ size = 22, className = "" }) => (
  <svg
    width={size}
    height={size}
    viewBox="0 0 24 24"
    fill="currentColor"
    className={className}
    aria-hidden
  >
    <path d="M18.244 2.25h3.308l-7.227 8.26 8.502 11.24H16.17l-5.214-6.817L4.99 21.75H1.68l7.73-8.835L1.254 2.25H8.08l4.713 6.231zm-1.161 17.52h1.833L7.084 4.126H5.117z" />
  </svg>
);

const normalizeSocialUrl = (url) => {
  if (!url || typeof url !== "string") return "";
  const trimmed = url.trim();
  if (!trimmed) return "";
  if (/^https?:\/\//i.test(trimmed)) return trimmed;
  return `https://${trimmed}`;
};

const getAgentSocialLinks = (agent) => {
  const items = [
    {
      key: "twitter",
      label: "X",
      url: normalizeSocialUrl(agent?.twitter_profile_URL),
      Icon: XIcon,
      colorClass: "text-[#000000]",
    },
    {
      key: "linkedin",
      label: "LinkedIn",
      url: normalizeSocialUrl(agent?.linkedIn_profile_URL),
      Icon: FaLinkedin,
      colorClass: "text-[#0A66C2]",
    },
    {
      key: "facebook",
      label: "Facebook",
      url: normalizeSocialUrl(agent?.facebook_profile_URL),
      Icon: FaFacebook,
      colorClass: "text-[#1877F2]",
    },
  ];
  return items.filter((item) => item.url);
};

const AgentProfileInfo = ({ AgentData, navigate }) => {
  const responce = isEmpty(AgentData);
  let averageRatings = 0;
  const [calls, setCalls] = useState(false);

  const getReviewsData = () => {
    for (var i = 0; i < AgentData?.reviews?.length; i++) {
      averageRatings += Number(AgentData?.reviews[i]?.star);
    }
    return (averageRatings /= Number(AgentData?.reviews?.length));
  };

  if (!responce) {
    if (AgentData?.reviews?.length !== 0) getReviewsData();
  }

  const agentProfileInfo = `
    ${AgentData?.job_title || "Agent"}
    ${AgentData?.agency_id?.principal_name
      ? ` | ${AgentData?.agency_id?.principal_name}`
      : ""
    }
     ${AgentData?.agency_id?.country
      ? ` at ${AgentData?.agency_id?.country}`
      : ""
    }
  `.trim();

  const calculateYearsFromCreatedAt = (dateValue) => {
    if (!dateValue) return null;
    const d = new Date(dateValue);
    if (Number.isNaN(d.getTime())) return null;
    const diffMs = Date.now() - d.getTime();
    if (diffMs < 0) return 0;
    const years = Math.ceil(diffMs / (365.25 * 24 * 60 * 60 * 1000));
    return `${Math.max(1, years)} years experience`;
  };
  const socialLinks = useMemo(
    () => getAgentSocialLinks(AgentData),
    [AgentData],
  );

  const displayRating = Number.isFinite(averageRatings) ? averageRatings : 0;
  const reviewCount = AgentData?.reviews?.length ?? 0;

  return (
    <div>
      <div
        className={`px-6 py-4 flex justify-center ${AgentData?.agency_id?.primary_color ? "" : "bg-black"}`}
        style={
          AgentData?.agency_id?.primary_color
            ? { backgroundColor: AgentData.agency_id.primary_color }
            : undefined
        }
      >
        <img
          src={AgentData?.agency_id?.agencyLargeLogo}
          alt="Red white"
          className="h-8 cursor-pointer"
          onClick={() => navigate(`/agency-profile/${AgentData?.agency_id?._id}`)}
        />
      </div>
      <img
        src={AgentData?.coverProfileImg}
        alt="Main"
        className="w-full h-[210px] md:h-[370px] object-cover"
      />
      <div className="bg-white flex justify-center py-10 px-2 md:px-0">
        <div className="container flex flex-col md:flex-row justify-between items-start md:items-center">
          <div className="flex items-center gap-6">
            <img
              src={AgentData?.profileImg}
              alt="Main"
              className="w-[100px] md:w-[135px] h-[100px] md:h-[135px] object-cover rounded-full border-4 border-gray-100"
            />
            <div className="flex flex-col gap-2">
              <div className="text-3xl font-bold">{AgentData?.name}</div>
              <div className="text-sm font-medium text-gray-600">
                {agentProfileInfo}
              </div>
              <div className="flex items-center text-sm font-medium text-gray-600">
                <BriefcaseBusiness size={20} />
                <span className="ml-2">{AgentData?.createdAt ? calculateYearsFromCreatedAt(AgentData?.createdAt) : 0} </span>
              </div>
              <div className="flex items-center text-sm font-medium text-gray-600">
                <Star fill="#FFB200" stroke="#FFB200" size={20} />
                <span className="ml-2 text-black">
                  {displayRating.toFixed(1)}
                </span>
                <span className="ml-2 font-normal">
                  ({reviewCount} reviews)
                </span>
              </div>
              {socialLinks.length > 0 && (
                <div className="flex items-center gap-3 mt-1">
                  {socialLinks.map(({ key, label, url, Icon, colorClass }) => (
                    <a
                      key={key}
                      href={url}
                      target="_blank"
                      rel="noopener noreferrer"
                      aria-label={`${AgentData?.name || "Agent"} on ${label}`}
                      title={label}
                      className={`inline-flex items-center justify-center transition-opacity hover:opacity-80 ${colorClass}`}
                    >
                      <Icon size={22} aria-hidden />
                    </a>
                  ))}
                </div>
              )}
            </div>
          </div>
          <div className="md:w-[294px] flex flex-col gap-3 mt-6 w-full">
            <button
              className="w-full flex justify-center items-center gap-2 text-sm font-medium rounded-full bg-[#097FE6] py-3 px-12 cursor-pointer text-white text-center"
              onClick={() => {
                navigate(`/appraisal/${AgentData?._id}`);
              }}
            >
              <span>Request a free appraisal</span>
            </button>

            <div className="flex items-center gap-3">
              <a
                className="flex-1 w-full flex justify-center items-center gap-2 text-sm font-medium rounded-full border border-[#959199] text-[#3D3B40] py-3 px-2 cursor-pointer text- text-center hover:border-[#097FE6] hover:text-[#097FE6]"
                href={`tel:${AgentData?.mobile_number}`}
              >
                <Phone size={16} />
                {calls ? (
                  <div className="">{`${AgentData?.mobile_number}`}</div>
                ) : (
                  <span className="">Call</span>
                )}
              </a>

              <a
                href={`https://wa.me/${AgentData?.mobile_number}`}
                target="_blank"
                rel="noopener noreferrer"
                className="flex-1 w-full flex justify-center items-center gap-2 text-sm font-medium rounded-full border border-[#959199] text-[#3D3B40] py-3 px-2 cursor-pointer text- text-center hover:border-[#097FE6] hover:text-[#097FE6]"
              >
                <MessageCircle size={16} />
                <span>Whatsapp</span>
              </a>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AgentProfileInfo;
